// Enrichment as Spec uses it: the per-turn caps, the charged-step header, the kinds Spec reveals, the credit format.

/** Spec's own bounds on paid enrichment in one turn (Enrich PR6). */
export const SPEC_ENRICH_LIMITS = {
  searchesPerTurn: 3,
  resultsPerTurn: 100,
  revealsPerTurn: 100,
  /** Details one Spec call may ask for (contacts x kinds, or URLs): its receipt must fit the stored-result cap. */
  revealsPerCall: 50,
  defaultSearchLimit: 25,
  requestTimeoutMs: 120_000,
  /** A reveal or LinkedIn read that can cost more than this asks first; up to it runs at once. */
  approveAboveMicrocredits: 10_000_000,
} as const;

/** The most the API stores for one tool result (the idempotency envelope); a Worker room check is bounded by it. */
export const SPEC_STORED_RESULT_MAX_BYTES = 16 * 1024;
/** The result itself, inside that envelope: what a receipt is trimmed to, and the most room a spend asks for. */
export const SPEC_STORED_RESULT_BUDGET_BYTES = SPEC_STORED_RESULT_MAX_BYTES - 1_024;

/** The step whose tool call spent enrichment credits; the Spec Worker sends it so usage shows under the run. */
export const SPEC_STEP_HEADER = 'X-Parcel-Spec-Step';

/** What Spec may reveal; phone is designed for (decision 19) and refused until a provider exists. */
export const SPEC_REVEAL_KINDS = ['email', 'profile'] as const;
export type SpecRevealKind = (typeof SPEC_REVEAL_KINDS)[number];

/** Enrichment credits for a card or a step label: 0.75, 0.10, 0.004. */
export function formatEnrichCredits(microcredits: number): string {
  return (Math.max(0, microcredits) / 1_000_000).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 3,
  });
}

const textList = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string' && entry.length > 0)
    : [];
const oneOrMany = (one: unknown, many: unknown): string[] => [
  ...new Set([...(typeof one === 'string' && one.length > 0 ? [one] : []), ...textList(many)]),
];

/** What a spend names, read one way by the worker's turn count and the API's card: ids once whatever their case. */
export function specEnrichSpendTargets(args: unknown): {
  ids: string[];
  kinds: string[];
  urls: string[];
} {
  const input =
    typeof args === 'object' && args !== null && !Array.isArray(args)
      ? (args as Record<string, unknown>)
      : {};
  return {
    ids: [...new Set(oneOrMany(input.id, input.ids).map((id) => id.toLowerCase()))],
    kinds: input.kinds === undefined ? ['email'] : [...new Set(textList(input.kinds))],
    urls: oneOrMany(input.url, input.urls),
  };
}
